<template>
  <div class="my-5 container">
    <div class="text-main">
      <h2 class="font-weight-bolder">{{ $t("header.map") }}</h2>
    </div>
    <div class="mt-5 map-container">
      <div v-if="fetching" class="data-loading">
        {{ $t("listing.loading") }}...
      </div>
      <l-map
        ref="map"
        id="map"
        style="width: 100%; height: 600px"
        :zoom="1"
        @ready="initializeMap"
        @zoomend="onZoomChange"
        @moveend="onMapMove"
      >
        <l-tile-layer :url="url" :attribution="attribution"></l-tile-layer>
        <l-marker
          v-for="marker in markers"
          :key="marker.id"
          :lat-lng="marker.position"
          :icon="marker.icon"
          @popupopen="onPopupOpen"
          @popupclose="onPopupClose"
          @ready="bindHoverEvents($event, markers.indexOf(marker))"
        >
          <component
            :is="marker.tooltip ? 'l-tooltip' : 'l-popup'"
            :options="marker.popupOptions || defaultPopupOptions"
          >
            <div
              v-html="marker.popupText"
              :class="marker.property ? 'popup-parent_container' : ''"
            ></div>
          </component>
        </l-marker>
      </l-map>
      <div v-if="mapInfo" class="map-info">
        {{ mapInfo }} | {{ markers.length }} {{ $t("listing.markersCount") }}
      </div>
    </div>
  </div>
</template>

<script>
import { LMap, LTileLayer, LMarker, LPopup, LTooltip } from "vue2-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import FullScreen from "leaflet.fullscreen";
import "leaflet.fullscreen/dist/Control.FullScreen.css";
import { mapState, mapActions, mapGetters } from "vuex";
import {
  getRadiusFromBounds,
  propertyPopupHtml,
  countryPopupHtml,
  worldPopupHtml,
  continentPopupHtml,
} from "../helpers/mapHelper";
import { currentConfigKey } from "../helpers/theme";
import { nuiteeSupplierId } from "../shared";
import { debounce } from "lodash";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: "/images/marker-icon.png",
  iconRetinaUrl: "/images/marker-icon.png",
  shadowUrl: "/images/marker-shadow.png",
  shadowRetinaUrl: "/images/marker-shadow.png",
});

export default {
  components: {
    LMap,
    LTileLayer,
    LMarker,
    LPopup,
    LTooltip,
  },
  data() {
    return {
      mapInfo: "",
      fetching: false,
      url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      attribution:
        '&copy; <a target="_blank" href="http://osm.org/copyright">OpenStreetMap</a> contributors',
      propertiesCountData: {},
      markers: [],
      markedProperties: null,
      isPopupOpen: false,
      activeMarkerIndex: null,
      defaultPopupOptions: {
        permanent: false,
        interactive: true,
        autoClose: false,
        closeOnClick: false,
        autoPan: true,
      },
      listId: null,
      mapConfig: {
        zoomLevel: 7,
        centreLatitude: "",
        centreLongitude: "",
      },
    };
  },
  computed: {
    ...mapState("property", ["properties"]),
    ...mapGetters("config", ["bookingEngineId"]),
    parsedQuery() {
      const { latitude, longitude, ratings, facilities, distributorType, property_id } =
        this.$route.query;
      return {
        latitude,
        longitude,
        propertyId: property_id,
        ratings: ratings ? ratings.split(",") : [],
        facilities: facilities ? facilities.split(",") : [],
        distributorType: distributorType ? distributorType.split(",") : [],
      };
    },
    isForBookingEngine() {
      const { latitude, longitude, ratings, facilities, distributorType } =
        this.$route.query;
      return !!(
        (latitude && longitude) ||
        ratings ||
        facilities ||
        distributorType
      );
    },
  },
  mounted() {
    this.listId = this.bookingEngineId;
    this.mapConfig = JSON.parse(JSON.stringify(currentConfigKey("mapConfig")));
    const { latitude, longitude } = this.$route.query;
    if (latitude && longitude) {
      this.mapConfig.centreLatitude = latitude;
      this.mapConfig.centreLongitude = longitude;
    }
    this.markedProperties = new Set();
    window.vueApp = this;
    this.$refs.map.mapObject.setMaxBounds([
      [-90, -180],
      [90, 180],
    ]);
  },
  methods: {
    ...mapActions("property", ["searchProperties", "countProperties"]),
    async initializeMap() {
      const map = this.$refs.map?.mapObject;
      if (map) {
        map.addControl(
          new FullScreen({
            position: "topright",
          })
        );
      }
      await this.setPropertiesUsingList();
    },
    onPopupOpen() {
      this.isPopupOpen = true;
    },
    onPopupClose() {
      this.isPopupOpen = false;
      this.activeMarkerIndex = null;
    },
    bindHoverEvents(marker, index) {
      marker.on("click", () => {
        const map = this.$refs.map.mapObject;
        map.setView(marker.getLatLng(), map.getZoom(), { animate: true });
        marker.openPopup();
      });
      marker.on("mouseover", () => {
        if (
          this.activeMarkerIndex === null ||
          this.activeMarkerIndex === index
        ) {
          marker.openPopup();
          this.activeMarkerIndex = index;
          const popupContent = marker.getPopup().getElement();
          if (popupContent) {
            popupContent.addEventListener("mouseout", (e) => {
              const relatedTarget = e.relatedTarget;
              if (
                !relatedTarget ||
                (!popupContent?.contains(relatedTarget) &&
                  !marker.getElement()?.contains(relatedTarget))
              ) {
                setTimeout(() => {
                  if (
                    !popupContent?.matches(":hover") &&
                    !marker.getElement()?.matches(":hover")
                  ) {
                    marker.closePopup();
                    this.activeMarkerIndex = null;
                  }
                }, 300);
              }
            });
          }
        }
      });
      marker.on("mouseout", (e) => {
        if (this.activeMarkerIndex === index) {
          const relatedTarget = e.relatedTarget;
          const popupContent = marker.getPopup()?.getElement();
          if (
            relatedTarget &&
            popupContent &&
            popupContent?.contains(relatedTarget)
          ) {
            return;
          }
          setTimeout(() => {
            if (
              popupContent &&
              !popupContent?.matches(":hover") &&
              !marker.getElement()?.matches(":hover")
            ) {
              marker.closePopup();
              this.activeMarkerIndex = null;
            }
          }, 300);
        }
      });
      if (this.markers[index]) {
        this.markers[index].marker = marker;
      }
    },
    async prepareCountData() {
      if (Object.keys(this.propertiesCountData).length === 0) {
        const response = await this.countProperties();
        const { data } = response.data;
        this.propertiesCountData = data;
      }
    },
    async setWorldMarkers() {
      await this.prepareCountData();
      let formattedNumber =
        this.propertiesCountData.world.toLocaleString("en-US");
      this.markers = [
        {
          _summary: true,
          position: [0, 0],
          popupText: worldPopupHtml({ formattedNumber }),
        },
      ];
      this.$nextTick(() => {
        setTimeout(() => {
          const map = this.$refs.map.mapObject;
          map.eachLayer((layer) => {
            if (
              layer instanceof L.Marker &&
              layer.getLatLng().lat === 0 &&
              layer.getLatLng().lng === 0
            ) {
              layer.openPopup();
            }
          });
        }, 100);
      });
    },
    async setContinentsMarkers() {
      await this.prepareCountData();
      const continentsWithProperties = (
        this.propertiesCountData.continent || []
      ).filter((c) => Number(c.listIdPropertyCount) > 0);
      this.markers = continentsWithProperties.map((continent) => {
        let formattedNumber = continent.listIdPropertyCount.toLocaleString("en-US");
        return {
          _summary: true,
          position: [continent.latitude, continent.longitude],
          popupText: continentPopupHtml({ formattedNumber, continent }),
        };
      });
      this.$nextTick(() => {
        setTimeout(() => {
          const map = this.$refs.map.mapObject;
          this.markers.forEach((markerData) => {
            map.eachLayer((layer) => {
              if (
                layer instanceof L.Marker &&
                layer.getLatLng().lat === markerData.position[0] &&
                layer.getLatLng().lng === markerData.position[1]
              ) {
                layer.openPopup();
              }
            });
          });
          map.setView([37, 3]);
        }, 100);
      });
    },
    async setContriesMarkers() {
      await this.prepareCountData();
      const countriesWithProperties = (
        this.propertiesCountData.country || []
      ).filter((c) => Number(c.listIdPropertyCount) > 0);
      this.markers = countriesWithProperties.map((country) => {
        let formattedNumber = country.listIdPropertyCount.toLocaleString("en-US");
        return {
          _summary: true,
          position: [country.latitude, country.longitude],
          popupText: countryPopupHtml({ formattedNumber, country }),
        };
      });
    },
    closeAllPopups() {
      const map = this.$refs.map.mapObject;
      map.closePopup();
    },
    setZoomLevel(zoom, latitude, longitude) {
      const map = this.$refs.map?.mapObject;
      if (!map) return;

      const z = Number(zoom);
      const lat = Number(latitude);
      const lng = Number(longitude);
      if (!Number.isFinite(z) || !Number.isFinite(lat) || !Number.isFinite(lng)) {
        return;
      }

      this.closeAllPopups();
      map.setView([lat, lng], z, { animate: true });
    },
    onZoomChange() {
      this.closeAllPopups();
      const map = this.$refs.map.mapObject;
      const zoom = map.getZoom();
      if (zoom <= 1 && zoom >= 0) {
        this.clearMarkers();
        this.setWorldMarkers();
      } else if (zoom <= 3 && zoom >= 2) {
        this.clearMarkers();
        this.setContinentsMarkers();
      } else if (zoom <= 6 && zoom >= 4) {
        this.clearMarkers();
        setTimeout(() => {
            this.setContriesMarkers()
        }, 100);
      } else if (zoom >= 7) {
        this.markers = this.markers.filter((marker) => !marker._summary);
        setTimeout(() => {
            this.handleMapMove();
        }, 100);
      }
    },
    displayProperties(properties) {
      const geoCodeData = properties.map((property) => {
        if (property.media) {
          property.media
            .map((m) => {
              m.order = m.sortOrderMain || Number.MAX_VALUE;
              return m;
            })
            .sort((a, b) => a.order - b.order)
            .slice(0, 4);
        }

        let ratingValue = property.googleReviews?.googleRating || 0;
        let ratingCount = property.googleReviews?.googleNumberOfReviews || 0;
        ratingValue = parseFloat(ratingValue.toFixed(1));

        return {
          id: property.id,
          name: property.name,
          ...property.geoCode,
          distributorType: property.distributorType,
          ratingValue,
          ratingCount,
          address: property.addresses?.[0] || null,
          image:
            property.media?.[0]?.url || "/images/property-default-image.jpg",
        };
      });

      geoCodeData.forEach((item) => {
        if (!this.markedProperties.has(item.id)) {
          const { id, latitude, longitude, distributorType, supplierId } = item;

          let labels = this.getDistributorLabels(
            distributorType,
            supplierId
          ).join(", ");

          const popupText = propertyPopupHtml({ item, labels, listId: this.listId });
          const isSpecialProperty =
            this.parsedQuery.propertyId &&
            String(id) === String(this.parsedQuery.propertyId);
          this.markers.push({
            id,
            position: [latitude, longitude],
            property: true,
            popupText,
            ...(isSpecialProperty && { icon: this.getSpecialMarkerIcon() }),
            popupOptions: {
              permanent: false,
              direction: "auto",
              autoPan: false,
            },
          });
          this.markedProperties.add(id);
        }
      });

      if (this.markers.length > 2000) {
        const removedMarkers = this.markers.splice(0, 200);
        const removedIds = removedMarkers.map(({ id }) => id);
        removedIds.forEach((id) => {
          this.markedProperties.delete(id);
        });
      }
    },
    onMapMove() {
      const map = this.$refs.map?.mapObject;
      this.mapInfo = `
                ${this.$t("listing.zoom")}: ${map.getZoom()} |
                ${this.$t("listing.center")}: ${map
        .getCenter()
        .lat.toFixed(5)}, ${map.getCenter().lng.toFixed(5)}`;
      this.handleMapMove();
    },

    handleMapMove: debounce(async function () {
      const map = this.$refs.map?.mapObject;
      if (!map) return;

      if (map.getZoom() <= 6) return;

      const { ratings, facilities, distributorType } = this.parsedQuery;
      const bounds = map.getBounds();
      const defaultRadius = getRadiusFromBounds(bounds);
      const { lat: latitude, lng: longitude } = map.getCenter();

      try {
        this.fetching = true;

        const payload = {
          ratings,
          facilities,
          distributorType,
          latitude,
          longitude,
          page: 1,
          perPage: 200,
          radius: Math.min(Math.max(parseInt(defaultRadius, 10), 200), 1000),
          list_id: this.listId,
        };

        const { content } = await this.searchProperties(payload);
        this.displayProperties(content);
      } catch (error) {
        console.error("Error fetching properties:", error);
      } finally {
        this.fetching = false;
      }
    }, 1000),

    async setPropertiesUsingList() {
      const map = this.$refs.map.mapObject;
      const center = map.getCenter();

      let {
        centreLatitude: lat,
        centreLongitude: lng,
        zoomLevel,
      } = this.mapConfig;
      let { ratings, facilities, distributorType, propertyId } = this.parsedQuery;
      const bounds = map.getBounds();
      const defaultRadius = getRadiusFromBounds(bounds);
      const r = parseInt(defaultRadius, 10) || 200;

      if (zoomLevel <= 6) {
        this.setZoomLevel(zoomLevel, lat || 0, lng || 0);
        return;
      }

      if (lat && lng && !this.isForBookingEngine) {
        map.setView([lat, lng], zoomLevel, {
          animate: true,
          duration: 1,
          easeLinearity: 0.25,
        });
        return;
      }

      this.fetching = true;
      let radius = Math.min(r, 1000);
      if (propertyId) {
        radius = 20;
      }

      const { content: properties } = await this.searchProperties({
        ratings,
        facilities,
        distributorType,
        page: 1,
        perPage: 200,
        radius,
        list_id: this.listId,
        ...(lat &&
          lng && {
            latitude: lat ?? center.lat,
            longitude: lng ?? center.lng,
          }),
      });
      this.fetching = false;

      if (!properties.length) return;
      const points = properties
        .filter((p) => p.geoCode?.latitude && p.geoCode?.longitude)
        .map((p) => [p.geoCode.latitude, p.geoCode.longitude]);

      if (points.length === 0) return;

      this.displayProperties(properties);
      this.$nextTick(() => {
        const boundsLatLng = L.latLngBounds(points);
        if (points.length === 1) {
          map.setView(points[0], zoomLevel, { animate: true });
        } else {
          map.fitBounds(boundsLatLng, {
            padding: [50, 50],
            maxZoom: 16,
            animate: true,
          });
        }
      });
    },
    clearMarkers() {
      this.markers = [];
      this.markedProperties.clear();
    },
    getSpecialMarkerIcon() {
      const url = "/images/marker-icon-special.png";
      return new L.Icon({
        iconUrl: url,
        iconRetinaUrl: url,
        shadowUrl: "/images/marker-shadow.png",
        shadowRetinaUrl: "/images/marker-shadow.png",
        iconSize: [50, 50],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [41, 41],
      });
    },
    getDistributorLabels(distributorType, supplierId) {
      const isNuitee = Number(supplierId) === nuiteeSupplierId;
      const distributorMap = {
        A: ["listing.instantBooking", "listing.realTime"],
        B: [
          isNuitee ? "listing.instantBookingNuitee" : "listing.instantBooking",
        ],
        C: ["listing.bookingOnRequest", "listing.verifiedByGoogle"],
        default: ["listing.bookingOnRequest", "listing.notVerified"],
      };
      let labels = distributorMap[distributorType] || distributorMap.default;

      if (currentConfigKey("hideRealTimeBadge", false)) {
        labels = labels.filter((key) => key !== "listing.realTime");
      }

      return labels.map((key) => this.$t(key));
    },
  },
};
</script>

<style scoped>
@import "../assets/css/map.css";
</style>
